import { isSafari } from "./browser-detection";

/**
 * Options pour les opérations de retry
 */
export interface RetryOptions {
  maxRetries?: number;
  initialDelay?: number;
  maxDelay?: number;
  backoffMultiplier?: number;
  retryable?: (error: unknown) => boolean;
}

/**
 * Retry avec backoff exponentiel
 * 
 * @param fn - Fonction à exécuter avec retry
 * @param options - Options de retry
 * @returns Résultat de la fonction
 * @throws Dernière erreur si toutes les tentatives échouent
 * 
 * @example
 * ```typescript
 * const result = await retryWithBackoff(
 *   () => supabase.auth.getSession(),
 *   { maxRetries: 3, initialDelay: 500 }
 * );
 * ```
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxRetries = 3,
    initialDelay = 500,
    maxDelay = 5000,
    backoffMultiplier = 2,
    retryable = () => true,
  } = options;

  let lastError: unknown;
  let delay = initialDelay;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Si ce n'est pas la dernière tentative et que l'erreur est retryable
      if (attempt < maxRetries - 1 && retryable(error)) {
        // Attendre avant de retry (backoff exponentiel)
        await new Promise((resolve) => setTimeout(resolve, delay));
        delay = Math.min(delay * backoffMultiplier, maxDelay);
        continue;
      }

      // Dernière tentative ou erreur non retryable, lancer l'erreur
      throw error;
    }
  }

  // Ne devrait jamais arriver ici, mais TypeScript le demande
  throw lastError;
}

/**
 * Retry spécifique pour les opérations de session Supabase
 * Utilise des paramètres optimisés pour Safari
 * 
 * @param fn - Fonction d'opération de session à exécuter
 * @param options - Options de retry (peuvent être surchargées)
 * @returns Résultat de la fonction
 * 
 * @example
 * ```typescript
 * const session = await retrySessionOperation(
 *   () => supabase.auth.getSession(),
 *   { maxRetries: 3 }
 * );
 * ```
 */
export async function retrySessionOperation<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const isSafariBrowser = isSafari();

  // Paramètres optimisés pour Safari (plus de retries, délais plus longs)
  const defaultOptions: RetryOptions = {
    maxRetries: isSafariBrowser ? 3 : 2,
    initialDelay: isSafariBrowser ? 500 : 300,
    maxDelay: 2000,
    backoffMultiplier: 1.5,
    retryable: (error) => {
      // Retry seulement pour les erreurs réseau/timeout
      if (error instanceof Error) {
        const message = error.message.toLowerCase();
        return (
          message.includes("network") ||
          message.includes("timeout") ||
          message.includes("failed to fetch") ||
          message.includes("fetch")
        );
      }
      return false;
    },
  };

  return retryWithBackoff(fn, { ...defaultOptions, ...options });
}

/**
 * Retry pour les opérations d'authentification
 * Utilise des paramètres spécifiques pour les opérations auth
 * 
 * @param fn - Fonction d'authentification à exécuter
 * @param options - Options de retry (peuvent être surchargées)
 * @returns Résultat de la fonction
 * 
 * @example
 * ```typescript
 * const { user } = await retryAuthOperation(
 *   () => authService.signIn({ email, password })
 * );
 * ```
 */
export async function retryAuthOperation<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const isSafariBrowser = isSafari();

  const defaultOptions: RetryOptions = {
    maxRetries: isSafariBrowser ? 2 : 1,
    initialDelay: isSafariBrowser ? 500 : 300,
    maxDelay: 1500,
    backoffMultiplier: 1.5,
    retryable: (error) => {
      // Retry seulement pour les erreurs réseau/timeout
      if (error instanceof Error) {
        const message = error.message.toLowerCase();
        return (
          message.includes("network") ||
          message.includes("timeout") ||
          message.includes("failed to fetch")
        );
      }
      return false;
    },
  };

  return retryWithBackoff(fn, { ...defaultOptions, ...options });
}

/**
 * Vérifie si une erreur est retryable (erreur réseau/timeout)
 * 
 * @param error - L'erreur à vérifier
 * @returns true si l'erreur est retryable
 */
export function isRetryableError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  const message = error.message.toLowerCase();
  return (
    message.includes("network") ||
    message.includes("timeout") ||
    message.includes("timed out") ||
    message.includes("failed to fetch") ||
    message.includes("fetch")
  );
}

